// Encounter planning (pure, Phaser-free): how many waves a room has and which
// enemies each wave spawns, from the node type, its difficulty within the sector
// and the sector's difficulty / enemy pool. The director turns a plan into spawns.

import type { Rng } from '../../core/rng';
import type { SectorDef } from '../../core/sector';
import type { EliteAffix, EnemyId, NodeType } from '../../core/types';

/** Threat cost of one enemy (a trooper is the unit). */
export const ENEMY_COST: Readonly<Record<EnemyId, number>> = {
  trooper: 1,
  hornet: 1,
  lancer: 1.6,
  bulwark: 2,
};
/** Extra cost of making an enemy elite. */
export const ELITE_EXTRA_COST = 2.5;
/** Per-wave caps so a wave never floods with one type. */
const TYPE_CAP: Readonly<Record<EnemyId, number>> = { trooper: 5, hornet: 4, lancer: 3, bulwark: 2 };
const MAX_PER_WAVE = 9;

export interface SpawnPlan {
  id: EnemyId;
  /** Flying enemies spawn on air points. */
  air: boolean;
  elite: EliteAffix[];
}

export interface WavePlan {
  spawns: SpawnPlan[];
  /** Total threat cost of the wave. */
  cost: number;
}

export interface EncounterInput {
  type: NodeType;
  /** 0..1 within the sector (node.difficulty). */
  difficulty: number;
  sector: Pick<SectorDef, 'index' | 'difficulty' | 'enemyPool' | 'eliteAffixes'>;
  /** Run challenge option: every combat wave includes an elite. */
  eliteSurge?: boolean;
}

/** True for node types that fight waves of enemies. */
export function isCombatRoom(type: NodeType): boolean {
  return type === 'start' || type === 'combat' || type === 'elite' || type === 'loot';
}

/** Budget multiplier from the sector's difficulty (hp/damage scaling is applied by enemies). */
function sectorScale(sector: EncounterInput['sector']): number {
  return 1 + (sector.difficulty - 1) * 0.6;
}

export function rollAffixes(rng: Rng, sector: EncounterInput['sector']): EliteAffix[] {
  const count = sector.index === 0 ? 1 : sector.index === 1 ? (rng.chance(0.5) ? 2 : 1) : 2;
  const pool = rng.shuffle(sector.eliteAffixes.length > 0 ? sector.eliteAffixes : (['shielded'] as EliteAffix[]));
  return pool.slice(0, Math.max(1, Math.min(count, pool.length)));
}

function pickEnemy(
  rng: Rng,
  input: EncounterInput,
  remaining: number,
  counts: Map<EnemyId, number>,
  allowed?: readonly EnemyId[],
): EnemyId | undefined {
  const early = input.sector.index === 0 && input.difficulty <= 0.2;
  return rng.weighted(input.sector.enemyPool, (e) => {
    if (allowed && !allowed.includes(e.id)) return 0;
    if ((counts.get(e.id) ?? 0) >= TYPE_CAP[e.id]) return 0;
    if (ENEMY_COST[e.id] > remaining + 0.35) return 0;
    // Heavy units are rarer in the very first rooms.
    if (early && (e.id === 'bulwark' || e.id === 'lancer')) return e.weight * 0.35;
    return e.weight;
  })?.id;
}

function fillWave(rng: Rng, input: EncounterInput, budget: number, wave: WavePlan, allowed?: readonly EnemyId[]): void {
  const counts = new Map<EnemyId, number>();
  for (const s of wave.spawns) counts.set(s.id, (counts.get(s.id) ?? 0) + 1);
  let remaining = budget;
  while (remaining > 0.5 && wave.spawns.length < MAX_PER_WAVE) {
    const id = pickEnemy(rng, input, remaining, counts, allowed);
    if (!id) break;
    counts.set(id, (counts.get(id) ?? 0) + 1);
    wave.spawns.push({ id, air: id === 'hornet', elite: [] });
    wave.cost += ENEMY_COST[id];
    remaining -= ENEMY_COST[id];
  }
  // Every wave has at least one ground threat so flyers never fight alone.
  if (!wave.spawns.some((s) => !s.air) && wave.spawns.length > 0) {
    wave.spawns[0] = { id: 'trooper', air: false, elite: wave.spawns[0].elite };
  }
}

function addElite(rng: Rng, input: EncounterInput, wave: WavePlan): void {
  const counts = new Map<EnemyId, number>();
  const id = pickEnemy(rng, input, 99, counts, ['trooper', 'lancer', 'bulwark', 'hornet']) ?? 'trooper';
  wave.spawns.push({ id, air: id === 'hornet', elite: rollAffixes(rng, input.sector) });
  wave.cost += ENEMY_COST[id] + ELITE_EXTRA_COST;
}

/** Waves for one room (empty for rooms without combat). Deterministic for a given rng state. */
export function planEncounter(rng: Rng, input: EncounterInput): WavePlan[] {
  if (!isCombatRoom(input.type)) return [];
  const d = Math.max(0, Math.min(1, input.difficulty));
  const scale = sectorScale(input.sector);
  const perWave = (3.2 + 4.4 * d) * scale;
  const waves: WavePlan[] = [];
  const newWave = (): WavePlan => {
    const w: WavePlan = { spawns: [], cost: 0 };
    waves.push(w);
    return w;
  };

  switch (input.type) {
    case 'start': {
      // Short intro: two light waves of the basic units.
      const basics: EnemyId[] = ['trooper', 'hornet'];
      fillWave(rng, input, 2.4 * scale, newWave(), basics);
      fillWave(rng, input, 3.4 * scale, newWave(), basics);
      break;
    }
    case 'elite': {
      fillWave(rng, input, perWave * 0.75, newWave());
      const finale = newWave();
      const elites = d >= 0.8 || (d >= 0.55 && input.sector.index >= 1) ? 2 : 1;
      for (let i = 0; i < elites; i++) addElite(rng, input, finale);
      fillWave(rng, input, perWave * (elites === 2 ? 0.35 : 0.6), finale);
      break;
    }
    case 'loot':
    case 'combat': {
      const count = input.type === 'loot' ? 2 : d < 0.3 ? 2 : d < 0.6 ? (rng.chance(0.5) ? 3 : 2) : 3;
      const factor = input.type === 'loot' ? 0.85 : 1;
      for (let i = 0; i < count; i++) {
        const w = newWave();
        if (input.eliteSurge) addElite(rng, input, w);
        fillWave(rng, input, perWave * factor * (0.85 + 0.15 * i) - (input.eliteSurge ? 1 : 0), w);
      }
      break;
    }
  }
  return waves.filter((w) => w.spawns.length > 0);
}

/** The next wave arrives once at most this many enemies of the current wave remain. */
export function advanceThreshold(wave: WavePlan): number {
  return wave.spawns.length >= 6 ? 2 : 1;
}

/** Blitz challenge time limit (35–50 s) from the encounter's total threat. */
export function blitzLimit(waves: readonly WavePlan[]): number {
  const total = waves.reduce((s, w) => s + w.cost, 0);
  return Math.round(Math.max(35, Math.min(50, 22 + total * 1.6)));
}
